import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { getAuth } from "firebase/auth";
import { includes } from "lodash";

import { getCloudFunctionUrl } from "./firebase.js";

type TeamType = "clinic" | "lab" | "admin";

type LegacyResponseHooks = {
  onAlert?: (message: string, options?: { autoClose?: boolean }) => void;
  onMiniAlert?: (message: string) => void;
  onCaptureException?: (err: unknown) => void;
  shouldCaptureException?: (code?: string) => boolean;
  onSignOut?: () => Promise<void> | void;
};

type LegacyInterceptorOptions = {
  exceptionList?: string[];
  hooks?: LegacyResponseHooks;
};

type CreateApiClientOptions = {
  baseURL: string;
  teamType: TeamType;
  teamIndex?: string;
  withLegacyInterceptor?: boolean;
  legacyInterceptorOptions?: LegacyInterceptorOptions;
  extraResponseInterceptor?: {
    onFulfilled?: (
      response: AxiosResponse,
    ) => AxiosResponse | Promise<AxiosResponse>;
    onRejected?: (error: any) => any;
  };
};

const DEFAULT_LEGACY_EXCEPTIONS = [
  "/api/v1/authenticates",
  "/api/v1/oauth/authorization",
];

// Guards buildLegacyResponseInterceptor's 401/403 branch below. A page
// typically fires several concurrent requests, so one genuinely broken
// session can make every one of them independently call onSignOut() and get
// reported. Module-level (not per-client) since a single app instance
// creates several clients (java server, java GCR, proxy, ...) that all
// share one onSignOut.
let isSigningOutFromAuthFailure = false;

/**
 * Adds Firebase authentication token to request headers along with team context headers.
 */
const addFirebaseAuthToken =
  (teamType: TeamType, teamIndex: string = "0") =>
  async (
    config: InternalAxiosRequestConfig & {
      __retriedAfterAuthRefresh?: boolean;
    },
  ) => {
    // retryOnceWithRefreshedAuthToken already attached a freshly-forced
    // token to this exact request before replaying it - don't refetch and
    // potentially clobber it with getIdToken()'s cached value here.
    if (config.__retriedAfterAuthRefresh) {
      return config;
    }
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (currentUser) {
      const firebaseToken = await currentUser.getIdToken();
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${firebaseToken}`;
      config.headers["X-Team-Type"] = teamType;
      config.headers["X-Team-Index"] = teamIndex;
    }
    return config;
  };

const buildLegacyResponseInterceptor = (
  options: LegacyInterceptorOptions = {},
) => {
  const { exceptionList = DEFAULT_LEGACY_EXCEPTIONS, hooks } = options;

  const onFulfilled = (res: AxiosResponse) => res;
  const onRejected = async (err: any) => {
    if (exceptionList.includes(err?.config?.url)) return Promise.reject(err);

    const status = err?.response?.status;
    const result = err?.response?.data?.result;
    const url = err?.config?.url;

    if (!status) return Promise.reject(err);

    switch (status) {
      case 400: {
        if (result) {
          switch (result.code) {
            case "ORDER-ERR-6":
              hooks?.onAlert?.("접수 대기 단계로 전환이 불가능합니다.");
              break;
            case "CODE-407":
              hooks?.onAlert?.(
                "수정할 수 없는 주문입니다.\n 채팅으로 문의해주세요.",
              );
              break;
            default:
              if (result.message === "TOOTH_FRAME_INFO_ALREADY_USE") {
                hooks?.onMiniAlert?.(
                  "주문에 사용된 데이터는 삭제가 불가능합니다.",
                );
              } else if (result[0]?.errorLog) {
                hooks?.onAlert?.(result[0].errorLog);
              }
              break;
          }
        }

        if (hooks?.onCaptureException) {
          const shouldCapture =
            hooks.shouldCaptureException === undefined
              ? true
              : hooks.shouldCaptureException(result?.code);
          if (shouldCapture) hooks.onCaptureException(err);
        }
        break;
      }
      case 401:
      case 403: {
        // retryOnceWithRefreshedAuthToken below already tries to silently
        // recover a merely-stale token on both 401 and 403 (not every
        // backend distinguishes "not authenticated" from "forbidden"
        // correctly - see that function's comment), so a request only
        // reaches this branch when the session is genuinely invalid (or the
        // retry itself couldn't run, e.g. no currentUser). Treat both
        // statuses the same way: sign the user out. A case for 403 alone
        // would leave a genuinely broken session on a backend that returns
        // 401 correctly with no sign-out and no toast - just an app that
        // keeps silently failing. Dedupe: several requests can fail around
        // the same moment, but they all mean the same thing - sign out
        // once.
        if (url !== "/api/v1/authenticates" && !isSigningOutFromAuthFailure) {
          isSigningOutFromAuthFailure = true;
          try {
            await hooks?.onSignOut?.();
          } finally {
            isSigningOutFromAuthFailure = false;
            return Promise.reject(err);
          }
        }
        break;
      }
      case 404: {
        if (result?.code === "CODE-402") {
          hooks?.onAlert?.("회수된 주문입니다.\n다른 주문을 이용해주세요.");
        }

        if (hooks?.onCaptureException) {
          const shouldCapture =
            hooks.shouldCaptureException === undefined
              ? true
              : hooks.shouldCaptureException(result?.code);
          if (shouldCapture) hooks.onCaptureException(err);
        }
        break;
      }
      case 500:
      case 502: {
        if (url === "/api/v1/oauth/authorization") {
          window.location.reload();
        }
        hooks?.onAlert?.(
          "일시적인 네트워크 오류로\n서비스에 접속할 수 없습니다.\n 서비스 관리자에게 문의 해주세요.",
          { autoClose: false },
        );
        hooks?.onCaptureException?.(err);
        break;
      }
      default:
        break;
    }

    return Promise.reject(err);
  };

  return { onFulfilled, onRejected };
};

/**
 * Retries a request exactly once with a force-refreshed Firebase ID token
 * when it fails with 401 or 403. addFirebaseAuthToken() attaches
 * getIdToken() without forceRefresh, so a token that's stale/about-to-expire
 * (Firebase proactively refreshes ~5min before the 1h expiry, but that
 * background refresh can itself hiccup - see auth/network-request-failed)
 * can reach the backend and get correctly rejected. Registered first
 * (response interceptors run in registration order) so this runs before any
 * other response interceptor - a merely-stale token gets silently fixed here
 * instead of being reported as a real auth failure by e.g. logError.
 *
 * 403 is included alongside 401 because apps/api-java's JwtFilter doesn't
 * reject an expired/invalid Firebase token itself - it just lets the
 * request through unauthenticated, and since WebSecurityConfig never wires
 * a real AuthenticationEntryPoint, Spring's legacy default
 * (Http403ForbiddenEntryPoint) answers with 403 for that case instead of
 * 401. So on this backend, "your token didn't authenticate" and "you're
 * authenticated but forbidden" are both flattened into the same status
 * code, and this retry can't tell them apart from the response alone - it
 * doesn't need to, since retrying a genuinely-forbidden request just costs
 * one extra round trip before falling through exactly as before (see
 * below). The real fix is wiring that entry point backend-side so 401 and
 * 403 mean what they're supposed to; this is the frontend mitigation for
 * as long as that's not done.
 *
 * Only retries once per request (config.__retriedAfterAuthRefresh) - a
 * session that's genuinely invalid (revoked, account withdrawn) will just
 * fail the same way again after the retry. That second failure is replayed
 * through the SAME client instance (not the bare axios() global), so it
 * still reaches every other interceptor in the chain - the legacy
 * interceptor's CODE-316/403 sign-out flow, and logError's reporting -
 * exactly as if this retry logic didn't exist. Only a merely-stale token
 * gets silently absorbed here; a genuinely broken session is exactly as
 * visible as before.
 */
const retryOnceWithRefreshedAuthToken = (client: AxiosInstance) => ({
  onFulfilled: (response: AxiosResponse) => response,
  onRejected: async (error: any) => {
    const status = error?.response?.status;
    const config = error?.config as
      | (InternalAxiosRequestConfig & {
          __retriedAfterAuthRefresh?: boolean;
        })
      | undefined;

    if (
      (status !== 401 && status !== 403) ||
      !config ||
      config.__retriedAfterAuthRefresh
    ) {
      return Promise.reject(error);
    }

    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      return Promise.reject(error);
    }

    try {
      const freshToken = await currentUser.getIdToken(true);
      config.__retriedAfterAuthRefresh = true;
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${freshToken}`;
      return client.request(config);
    } catch {
      return Promise.reject(error);
    }
  },
});

const createApiClient = (options: CreateApiClientOptions): AxiosInstance => {
  const {
    baseURL,
    teamType,
    teamIndex = "0",
    withLegacyInterceptor = false,
    legacyInterceptorOptions,
    extraResponseInterceptor,
  } = options;

  const client = axios.create({ baseURL });
  client.interceptors.request.use(
    addFirebaseAuthToken(teamType, teamIndex),
    (error) => Promise.reject(error),
  );

  const { onFulfilled: retryOnFulfilled, onRejected: retryOnRejected } =
    retryOnceWithRefreshedAuthToken(client);
  client.interceptors.response.use(retryOnFulfilled, retryOnRejected);

  if (withLegacyInterceptor) {
    const { onFulfilled, onRejected } = buildLegacyResponseInterceptor(
      legacyInterceptorOptions,
    );
    client.interceptors.response.use(onFulfilled, onRejected);
  }

  if (extraResponseInterceptor) {
    client.interceptors.response.use(
      extraResponseInterceptor.onFulfilled,
      extraResponseInterceptor.onRejected,
    );
  }

  return client;
};

const buildDeprecatedPrinterInterceptor = () => ({
  onFulfilled: (res: AxiosResponse) => res,
  onRejected: async (err: any) => {
    if (err.response) {
      switch (err.response.status) {
        case 500:
        case 502: {
          if (includes(err.config.url, "/api/v1/printer")) {
            return Promise.reject(err);
          }
          if (
            includes(err.config.url, "/printerAPI/plug/getSmartPlugInformation")
          ) {
            return Promise.reject(err);
          }
          if (
            includes(err.config.url, "/printerAPI/printer/getPrinterQRCode")
          ) {
            return Promise.reject(err);
          }
          break;
        }
        default:
          break;
      }
    }
    return Promise.reject(err);
  },
});

const createCloudFunctionClient = (
  functionName: string,
  teamType: TeamType,
  teamIndex: string = "0",
) =>
  createApiClient({
    baseURL: getCloudFunctionUrl(functionName),
    teamType,
    teamIndex,
  });

export type { TeamType, LegacyInterceptorOptions, LegacyResponseHooks };
export {
  createApiClient,
  buildLegacyResponseInterceptor,
  buildDeprecatedPrinterInterceptor,
  createCloudFunctionClient,
};
